/**
 * Reusable card for displaying an AI-generated question in preview lists.
 * Used by AIGenerateDialog and AddQuestionWithAIDialog.
 * When editable and onFieldChange are provided, question text, options, correct answer, and detail answer are editable.
 */
import React, { useState } from 'react';
import { Paper, Box, Typography, Collapse, Button, Chip, TextField, MenuItem, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReplayIcon from '@mui/icons-material/Replay';
import MathRenderer from '../common/MathRenderer';
import MathEditor from './MathEditor';
import AnswerEvaluationFields from './AnswerEvaluationFields';
import { QuestionImage } from '../common/QuestionImage';

const QUESTION_TYPE_LABELS = {
  MULTIPLE: 'Multiple Choice',
  TRUE_FALSE: 'True/False',
  SHORT_ANSWER: 'Short Answer',
  ESSAY: 'Essay',
};

export function getQuestionTypeLabel(type) {
  return QUESTION_TYPE_LABELS[type] || type;
}

export default function AIGeneratedQuestionCard({
  question,
  index,
  isSelected,
  onToggle,
  onViewPreview,
  showDetailAnswer = true,
  editable = false,
  onFieldChange,
  feedbackTags = [],
  selectedFeedbackTags = [],
  feedbackNote = '',
  onToggleFeedbackTag,
  onFeedbackNoteChange,
  regenerationInstruction = '',
  onRegenerationInstructionChange,
  onRegenerate,
  regenerating = false,
  allowDiagramVisual = false,
  allowRealWorldVisual = false,
  visualInstruction = '',
  onVisualInstructionChange,
  onGenerateVisual,
  onRemoveVisual,
  visualGenerating = '',
  visualError = '',
}) {
  const [detailExpanded, setDetailExpanded] = useState(false);
  const safeOptions = Array.isArray(question.options) ? question.options : [];
  const hasDetailAnswer = showDetailAnswer && (editable || (question.detail_answer && String(question.detail_answer).trim()));
  const visualUrl = question.image_proxy_url || question.image_url || '';
  const showVisualControls = editable && (allowDiagramVisual || allowRealWorldVisual || visualUrl);
  const visualNotice = question._visual_notice || '';
  const visualWarning = question._visual_warning || '';
  const visualSkipped = question._visual_status === 'skipped';
  const safeVisualInstruction = visualInstruction || '';

  const handleOptionChange = (optIndex, value) => {
    if (!onFieldChange) return;
    const next = [...safeOptions];
    while (next.length <= optIndex) next.push('');
    next[optIndex] = value;
    onFieldChange('options', next);
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.25, sm: 2 },
        mb: 2,
        border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
        cursor: editable ? 'default' : 'pointer',
        '&:hover': { borderColor: '#1976d2' },
        overflow: 'hidden',
      }}
      onClick={() => !editable && onToggle?.(index)}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggle?.(index)}
            onClick={(e) => e.stopPropagation()}
            style={{ marginTop: 4 }}
          />
          <Typography variant="subtitle2" color="primary" sx={{ flex: 1 }}>
            Question {index + 1}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }} onClick={(e) => editable && e.stopPropagation()}>
          <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2">
              <strong>Type:</strong> {getQuestionTypeLabel(question.question_type)}
            </Typography>
            {question.topic_name && (
              <Typography variant="body2" color="text.secondary">
                <strong>Topic:</strong> {question.topic_name}
              </Typography>
            )}
          </Box>

          {editable && onFieldChange ? (
            <>
              <Box onClick={(e) => e.stopPropagation()} sx={{ mb: 1.5 }}>
                <MathEditor
                  value={question.question_text ?? ''}
                  onChange={(val) => onFieldChange('question_text', val)}
                  label="Question text"
                  placeholder="Enter question text..."
                  helperText="Use the math button (∑) to insert mathematical formulas."
                  minRows={2}
                  fullWidth
                />
              </Box>
              {question.question_type === 'MULTIPLE' && (
                <Box sx={{ ml: 0, mb: 1.5 }} onClick={(e) => e.stopPropagation()}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Options
                  </Typography>
                  {[0, 1, 2, 3].map((optIndex) => (
                    <Box
                      key={optIndex}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '24px minmax(0, 1fr)', sm: '24px minmax(0, 1fr)' },
                        alignItems: 'flex-start',
                        gap: 1,
                        mb: 0.75,
                        minWidth: 0,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 20, pt: 1.5 }}>{String.fromCharCode(65 + optIndex)}.</Typography>
                      <MathEditor
                        value={safeOptions[optIndex] ?? ''}
                        onChange={(val) => handleOptionChange(optIndex, val)}
                        label=""
                        placeholder="Option text..."
                        minRows={1}
                        fullWidth
                        showPreview={true}
                      />
                    </Box>
                  ))}
                </Box>
              )}
              <Box onClick={(e) => e.stopPropagation()} sx={{ mb: 1.5 }}>
                {question.question_type === 'MULTIPLE' ? (
                  (() => {
                    const opts = safeOptions.map((o) => String(o ?? '').trim()).filter(Boolean);
                    const current = typeof question.correct_answer === 'string' ? question.correct_answer.trim() : (question.correct_answer ?? '');
                    const isNotAnOption = Boolean(current) && !opts.includes(current);
                    return (
                      <TextField
                        select
                        label="Correct answer"
                        size="small"
                        fullWidth
                        value={current}
                        onChange={(e) => onFieldChange('correct_answer', e.target.value)}
                        disabled={opts.length === 0}
                        error={isNotAnOption}
                        helperText={
                          opts.length === 0
                            ? 'Add options above first, then select the correct answer'
                            : isNotAnOption
                              ? `"${current}" is not one of the options — select a valid option`
                              : 'Select the option that is the correct answer'
                        }
                      >
                        <MenuItem value="">—</MenuItem>
                        {isNotAnOption && (
                          <MenuItem value={current}>{current} (not in options)</MenuItem>
                        )}
                        {opts.map((opt, i) => (
                          <MenuItem key={`${opt}-${i}`} value={opt}>{opt}</MenuItem>
                        ))}
                      </TextField>
                    );
                  })()
                ) : (
                  <MathEditor
                    value={question.correct_answer ?? ''}
                    onChange={(val) => onFieldChange('correct_answer', val)}
                    label="Correct answer"
                    placeholder="Enter the correct answer"
                    helperText="Use the math button (∑) for formulas."
                    minRows={1}
                    fullWidth
                    showPreview={true}
                  />
                )}
              </Box>
              <AnswerEvaluationFields
                questionType={question.question_type}
                value={question.answer_evaluation}
                onChange={(val) => onFieldChange('answer_evaluation', val)}
              />
              <Box onClick={(e) => e.stopPropagation()} sx={{ mb: 1 }}>
                <MathEditor
                  value={question.detail_answer ?? ''}
                  onChange={(val) => onFieldChange('detail_answer', val)}
                  label="Detailed solution"
                  placeholder="Step-by-step solution..."
                  helperText="Use the math button (∑) to insert mathematical formulas."
                  minRows={3}
                  fullWidth
                />
              </Box>
            </>
          ) : (
            <>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <MathRenderer content={question.question_text} />
              </Typography>
              {question.question_type === 'MULTIPLE' && safeOptions.length > 0 && (
                <Box sx={{ ml: 2 }}>
                  {safeOptions.map((option, optIndex) => (
                    <Box key={optIndex} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.5 }}>
                      <Typography component="span" variant="body2" sx={{ fontWeight: 600, minWidth: 20, flexShrink: 0 }}>
                        {String.fromCharCode(65 + optIndex)}.
                      </Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <MathRenderer content={option} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
              {question.correct_answer && (
                <Typography variant="body2" sx={{ mt: 1, color: 'success.main' }}>
                  <strong>Answer:</strong> <MathRenderer content={question.correct_answer} />
                </Typography>
              )}
              {hasDetailAnswer && !editable && (
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    size="small"
                    startIcon={detailExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={() => setDetailExpanded((v) => !v)}
                    sx={{ textTransform: 'none' }}
                  >
                    {detailExpanded ? 'Hide' : 'Show'} detailed solution
                  </Button>
                  <Collapse in={detailExpanded}>
                    <Box sx={{ mt: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <MathRenderer content={question.detail_answer} />
                    </Box>
                  </Collapse>
                </Box>
              )}
            </>
          )}

          {onViewPreview && (
            <Button
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={(e) => { e.stopPropagation(); onViewPreview(question); }}
              sx={{ mt: 1, textTransform: 'none', width: { xs: '100%', sm: 'auto' } }}
            >
              View full
            </Button>
          )}

          {(visualUrl || showVisualControls) && (
            <Box sx={{ mt: 1.5 }} onClick={(e) => e.stopPropagation()}>
              {visualUrl && (
                <QuestionImage question={question} alt="Generated visual" />
              )}
              {visualNotice && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  {visualNotice}
                </Typography>
              )}
              {visualWarning && (
                <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 0.75 }}>
                  {visualWarning}
                </Typography>
              )}
              {showVisualControls && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' }, gap: 1, alignItems: 'start' }}>
                  {!visualSkipped ? (
                    <TextField
                      label={allowRealWorldVisual ? 'Visual subject' : 'Visual instruction'}
                      value={safeVisualInstruction}
                      onChange={(event) => onVisualInstructionChange?.(event.target.value)}
                      size="small"
                      fullWidth
                      placeholder={allowRealWorldVisual ? 'Example: numeric keypad with digit buttons' : 'Example: right-angled triangle with sides 3, 4, 5'}
                      helperText={visualError || `${safeVisualInstruction.length}/500`}
                      error={Boolean(visualError)}
                    />
                  ) : (
                    <Box />
                  )}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {allowDiagramVisual && !visualSkipped && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => onGenerateVisual?.('diagram')}
                        disabled={Boolean(visualGenerating)}
                      >
                        {visualGenerating === 'diagram' ? 'Generating...' : visualUrl ? 'Regenerate Diagram' : 'Generate Diagram'}
                      </Button>
                    )}
                    {allowRealWorldVisual && !visualSkipped && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => onGenerateVisual?.('real_world')}
                        disabled={Boolean(visualGenerating) || !safeVisualInstruction.trim()}
                      >
                        {visualGenerating === 'real_world' ? 'Generating...' : visualUrl ? 'Regenerate Image' : 'Generate Image'}
                      </Button>
                    )}
                    {visualUrl && (
                      <Button
                        variant="text"
                        color="error"
                        size="small"
                        onClick={onRemoveVisual}
                        disabled={Boolean(visualGenerating)}
                      >
                        Remove Visual
                      </Button>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {editable && (feedbackTags.length > 0 || onRegenerate) && (
            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }} onClick={(e) => e.stopPropagation()}>
              {feedbackTags.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    Issue with this generated question? <Typography component="span" variant="caption" color="text.secondary">(optional)</Typography>
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                    {feedbackTags.map((tag) => {
                      const selected = selectedFeedbackTags.includes(tag.value);
                      return (
                        <Chip
                          key={tag.value}
                          label={tag.label}
                          size="small"
                          color={selected ? 'primary' : 'default'}
                          variant={selected ? 'filled' : 'outlined'}
                          onClick={() => onToggleFeedbackTag?.(tag.value)}
                        />
                      );
                    })}
                  </Box>
                  {selectedFeedbackTags.length > 0 && (
                    <TextField
                      label="Optional note for this question"
                      value={feedbackNote}
                      onChange={(event) => onFeedbackNoteChange?.(event.target.value)}
                      size="small"
                      fullWidth
                      multiline
                      minRows={1}
                      helperText={`${feedbackNote.length}/500`}
                      sx={{ mb: 1 }}
                    />
                  )}
                </>
              )}
              {onRegenerate && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                  <TextField
                    label="Instruction for regenerating this question"
                    value={regenerationInstruction}
                    onChange={(event) => onRegenerationInstructionChange?.(event.target.value)}
                    size="small"
                    fullWidth
                    placeholder="Example: make it easier and avoid LCM word-problem language"
                    helperText={`${regenerationInstruction.length}/500`}
                  />
                  <Button
                    variant="outlined"
                    startIcon={regenerating ? <CircularProgress size={16} /> : <ReplayIcon />}
                    onClick={onRegenerate}
                    disabled={regenerating}
                    sx={{ minWidth: 150, mt: { xs: 0, md: 0.25 } }}
                  >
                    Regenerate
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
}
